/**
 * domains/ai/thesis2/fallback.ts — MER Algo Footprints v7.1H
 *
 * Generates a deterministic fallback StructuredThesis when AI is
 * unavailable, returned invalid JSON, or failed validation.
 *
 * The fallback still reads real engine evidence — it just synthesizes
 * deterministically instead of using AI.
 *
 * audit.validation_status = 'FALLBACK' always.
 */

import { nanoid } from 'nanoid';
import { APP_VERSION } from '../../../config/version';
import { PROMPT_VERSION } from './prompt';
import { SCHEMA_VERSION } from './schema';
import {
  StructuredThesis, ThesisState, ActionPosture, ThesisBias,
  ThesisEvidenceInput, ConfidenceBreakdown,
} from './types';
import { GateEvaluation } from './gates';
import { detectThesisChanges } from './validator';

// ─── Deterministic posture from evidence ─────────────────────────────────────

function inferPosture(input: ThesisEvidenceInput, state: ThesisState): ActionPosture {
  if (state === 'INVALIDATED' || state === 'WAIT') return 'NO_TRADE';
  if (state === 'HIGH_RISK') return 'REDUCE_RISK';
  const topSetup = input.setups[0];
  if (!topSetup) return 'NEUTRAL';
  if (topSetup.dir === 'long')  return 'LONG';
  if (topSetup.dir === 'short') return 'SHORT';
  return 'NEUTRAL';
}

function inferBias(input: ThesisEvidenceInput): ThesisBias {
  const smcBias = input.smc?.bias;
  if (smcBias === 'bullish') return 'bullish';
  if (smcBias === 'bearish') return 'bearish';
  // Fallback to setup direction
  const dir = input.setups[0]?.dir;
  if (dir === 'long')  return 'bullish';
  if (dir === 'short') return 'bearish';
  return 'neutral';
}

// ─── Main fallback builder ────────────────────────────────────────────────────

export function buildFallbackThesis(
  input:          ThesisEvidenceInput,
  gates:          GateEvaluation,
  confidence:     ConfidenceBreakdown,
  evidenceHash:   string,
  fallbackReason: string,
): StructuredThesis {
  const thesis_id   = `fallback-${nanoid(8)}`;
  const timestamp   = Date.now();
  const topSetup    = input.setups[0] ?? null;
  const smc         = input.smc;
  const regime      = input.regime;
  const topTrap     = input.traps[0] ?? null;

  // Determine state
  const thesis_state: ThesisState = gates.downgraded_to ??
    (gates.passed ? 'DEVELOPING' : 'WAIT'); // fallback never claims ACTIONABLE
  const action_posture = inferPosture(input, thesis_state);
  const bias           = inferBias(input);

  // Summary
  const regimeLabel = regime?.label ?? 'unknown regime';
  const smcLabel    = smc ? `${smc.bias} structure` : 'no structure data';
  const summary = topSetup
    ? `Engine signals: ${smcLabel} with ${topSetup.type} setup. ${regimeLabel}. ` +
      `Deterministic analysis — AI unavailable.`
    : `Engine signals: ${smcLabel}. ${regimeLabel}. No actionable setup. ` +
      `Deterministic analysis — AI unavailable.`;

  const decision =
    thesis_state === 'DEVELOPING' && topSetup
      ? `Setup developing: ${topSetup.type} — confirm before entry.`
      : thesis_state === 'HIGH_RISK'
        ? `High risk flags active — reduce or avoid exposure.`
        : `No edge detected — stand aside.`;

  const result: StructuredThesis = {
    thesis_id,
    symbol:    input.symbol,
    timeframe: input.timeframe,
    timestamp,

    thesis_state,
    action_posture,
    is_actionable: false,   // fallback is never marked actionable

    setup_classification: topSetup?.type ?? 'unknown',
    setup_name:           topSetup
      ? topSetup.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : 'No Setup',

    summary,
    decision,
    bias,
    conviction:    Math.round(confidence.overall_confidence * 0.7),   // discount for fallback
    actionability: Math.round(confidence.readiness_score * 0.6),

    entry_plan: {
      setup_type:          topSetup?.type ?? 'none',
      entry_trigger:       topSetup?.entry ?? 'No entry trigger — await AI restoration',
      entry_price_hint:    topSetup?.entryPrice?.toFixed(2) ?? null,
      confirmation_needed: topSetup?.confirmation ?? 'AI analysis required',
      timing_note:         null,
    },

    risk_plan: {
      invalidation_level:  topSetup?.invalidation ?? 'Not determinable without AI',
      invalidation_reason: 'Price action structural level — review with AI',
      stop_quality:        input.risk?.stopQuality ?? 0,
      trap_conflict:       input.risk?.trapConflict ?? false,
      regime_conflict:     input.risk?.regimeConflict ?? false,
      primary_risk_note:   topTrap
        ? `Active ${topTrap.severity} trap: ${topTrap.reason}`
        : gates.failures[0] ?? 'Insufficient evidence for clean setup',
    },

    targets: {
      target_1:     topSetup?.t1 ?? null,
      target_2:     topSetup?.t2 ?? null,
      risk_reward:  input.risk?.expectedR ?? null,
      target_logic: topSetup?.t1 ? 'From setup engine level calculation' : 'Not determinable',
    },

    management: {
      max_hold_note:          'Exit per session end or invalidation',
      scale_note:             null,
      re_entry_note:          null,
      uncertainty_disclosure: 'AI layer unavailable. Deterministic signals only. Do not rely on this for execution decisions.',
    },

    evidence: {
      smc_read:       smc ? `${smc.bias} bias · ${smc.biasStrength} structure` : 'SMC not computed',
      regime_read:    regime ? `${regime.label} · ${regime.posture}` : 'Regime not computed',
      liquidity_read: input.zones.length
        ? `${input.zones.length} zones · ${input.zones.filter(z => z.sig === 'High').length} high-sig`
        : 'No zones',
      trap_read:      topTrap ? `${topTrap.type} · ${topTrap.severity} · ${topTrap.reason}` : 'No active traps',
      gamma_read:     input.gammaRead ?? 'N/A — no options data',
      setup_read:     topSetup ? `${topSetup.type} · ${topSetup.dir} · conf=${topSetup.confidence}%` : 'No setup',
      supporting_factors: gates.failures.length === 0
        ? ['Engine signals computed', 'Structure present', 'Deterministic analysis complete']
        : [],
      conflict_factors:   gates.failures.slice(0, 3),
      key_drivers:        [smcLabel, regimeLabel, topTrap ? `Trap: ${topTrap.type}` : 'No traps'],
      what_changed:       ['AI layer unavailable — deterministic fallback activated'],
    },

    confidence,

    // change_detection filled after full object is built — see below
    change_detection: {
      changed: false, change_type: 'NO_MATERIAL_CHANGE' as const,
      material_changes: [], previous_thesis_state: null,
      previous_posture: null, previous_bias: null, conviction_delta: null,
    },

    audit: {
      thesis_id,
      generated_at:      timestamp,
      model_id:          'deterministic-fallback',
      source_version:    APP_VERSION,
      prompt_version:    PROMPT_VERSION,
      schema_version:    SCHEMA_VERSION,
      input_hash:        evidenceHash,
      gate_results:      gates.results,
      validation_status: 'FALLBACK',
      fallback_reason:   fallbackReason,
      generation_ms:     null,
      repair_attempts:   0,
    },
  };

  // FIX: pass the fully assembled object so detectThesisChanges has real risk_plan etc.
  result.change_detection = detectThesisChanges(result, input.priorThesis);
  return result;
}
